import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Typography } from '@mui/material';
import { Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIInput from '@/shared/components/forms/WaveUIInput';
import Payment from '@/shared/types/order/payment.type';
import formatCurrency from '@/shared/utils/formatCurrency';

import RefundDetailsBox from './components/RefundDetailsBox';

type IssueRefundDialogProps = {
  payment: Payment;
  errors: string[];
  onClose: () => void;
  proceed: (payment: Payment, amount: number) => void;
};

const IssueRefundDialog: FC<IssueRefundDialogProps> = ({
  payment,
  errors,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['payments', 'common']);

  const [refundAmount, setRefundAmount] = useState('');
  const [formError, setFormError] = useState<Record<string, string>>({});

  const onValidate = () => {
    const newError: Record<string, string> = {};

    if (!refundAmount) {
      newError.amount = t('payments:refund_amount_required');
    }

    if (refundAmount && parseFloat(refundAmount) <= 0) {
      newError.amount = t('payments:refund_amount_positive');
    }

    if (
      refundAmount &&
      parseFloat(refundAmount) >
        parseFloat(payment.transaction_amount) -
          parseFloat(payment.amount_refunded || '0')
    ) {
      const remainingAmount = formatCurrency(
        parseFloat(payment.transaction_amount) -
          parseFloat(payment.amount_refunded || '0'),
      );

      newError.amount = `${t('payments:refund_amount_exceeds_remaining_amount')}: ${remainingAmount}`;
    }

    setFormError(newError);
    return !Object.keys(newError).length;
  };

  if (errors && errors?.length > 0) {
    return (
      <ModalNotification
        size='sm'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: t('payments:issue_refund'),
          content: (
            <Stack spacing={2} useFlexGap>
              <Typography>{t('payments:failed_to_issue_refund')}</Typography>
              {errors.map((error) => (
                <Alert key={error} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('payments:issue_refund'),
        content: (
          <Stack sx={{ py: 1 }} spacing={4}>
            <Stack spacing={1}>
              <Typography variant='body1'>
                {t('payments:refund_amount_description')}
              </Typography>

              <RefundDetailsBox payment={payment} />
            </Stack>

            <WaveUIInput
              name='amount'
              label={t('payments:refund_amount')}
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              placeholder={t('payments:refund_amount_placeholder')}
              error={!!formError.amount}
              helperText={formError.amount}
              type='number'
              required
            />
          </Stack>
        ),
        actions: [
          {
            label: t('common:cancel'),
            onClick: onClose,
          },
          {
            label: t('payments:refund'),
            variant: 'contained',
            onClick: () => {
              const isValid = onValidate();

              if (!isValid) {
                return;
              }
              proceed(payment, parseFloat(refundAmount));
            },
          },
        ],
      }}
    />
  );
};

export default IssueRefundDialog;
